// Per-hospital color helpers.
//
// Each hospital carries a `color` (stored on the hospital record, editable on the Hospitals page). If
// none is set yet, a stable default is derived from the hospitalId so every hospital still shows a
// distinct, consistent color out of the box. The Board uses:
//   - the BASE color for the section outline,
//   - a DARKER shade for the hospital header bar,
//   - a FADED (low-alpha) version behind the cases.

const HEX6 = /^#[0-9a-fA-F]{6}$/;
const NEUTRAL = '#7a8699';

function hslToHex(h: number, s: number, l: number): string {
  s /= 100; l /= 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const c = l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return Math.round(255 * c).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

// Deterministic distinct color from an id (used when a hospital has no explicit color yet).
export function defaultHospitalColor(hospitalId: string): string {
  let h = 0;
  for (let i = 0; i < hospitalId.length; i++) h = (h * 31 + hospitalId.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  const sat = 45 + (h >> 3) % 20;   // 45–64
  const light = 46 + (h >> 7) % 10; // 46–55
  return hslToHex(hue, sat, light);
}

export function colorForHospital(h: { hospitalId: string; color?: string } | undefined | null): string {
  if (!h || !h.hospitalId) return NEUTRAL;
  return h.color && HEX6.test(h.color) ? h.color : defaultHospitalColor(h.hospitalId);
}

export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const m = HEX6.test(hex) ? hex : NEUTRAL;
  return { r: parseInt(m.slice(1, 3), 16), g: parseInt(m.slice(3, 5), 16), b: parseInt(m.slice(5, 7), 16) };
}

// Mix toward black for the header bar.
export function darkenHex(hex: string, amount = 0.35): string {
  const { r, g, b } = hexToRgb(hex);
  const d = (c: number) => Math.max(0, Math.min(255, Math.round(c * (1 - amount))));
  return `#${[d(r), d(g), d(b)].map(c => c.toString(16).padStart(2, '0')).join('')}`;
}

// Low-alpha wash for the section background behind the cases.
export function fadeHex(hex: string, alpha = 0.12): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
